import { useCallback, useEffect, useState } from "react";
import { AppShell } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Chip } from "../components/Status";
import { QueueTable, neighbors } from "../components/Queue";
import type { QueueItem } from "../components/Queue";
import { chatApi } from "../chat/api";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { errText, tasksApi } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 일감 › 대기열(#/queue) — t-in-port-work-queue ══
   자동 실행을 켠 이슈만 선다(`agent_requested_at`). 옮긴 뒤에는 늘 다시 읽는다 — 낙관적으로 옮겨 두면
   서버가 거절했을 때 되돌리는 길이 하나 더 생긴다(보드와 같은 규칙). */

export function QueuePage() {
  const [rows, setRows] = useState<QueueItem[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [moveErr, setMoveErr] = useState<string | null>(null);
  const [why, setWhy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      // 단계 띠가 쓰는 `QueueRow` 는 같은 응답의 좁은 모양이다 — 이 화면은 이슈 칸까지 읽는다.
      setRows((await chatApi.queue()) as unknown as QueueItem[]);
      setErr(null);
    } catch (e) {
      setErr(errText(e));
    }
  }, []);
  useEffect(() => {
    void load();
    // 프로젝트 이름은 곁가지 — 못 받으면 id 로 그린다.
    projectApi.list().then(setProjects, () => undefined);
  }, [load]);

  async function move(from: number, to: number) {
    if (!rows || busy) return;
    const { prevId, nextId } = neighbors(rows.map((r) => r.task.id), from, to);
    setBusy(true);
    setMoveErr(null);
    try {
      await tasksApi.setQueueOrder(rows[from].task.id, prevId, nextId);
    } catch (e) {
      setMoveErr(errText(e));
    } finally {
      await load();
      setBusy(false);
    }
  }

  const name = (id: string | null | undefined) => projects.find((p) => p.id === id)?.name ?? id ?? "";
  const waiting = rows?.filter((r) => r.track === null).length ?? 0;

  return (
    <AppShell route="/queue">
      <PageHead
        title={tx("nav.queue")}
        lead={<p>{tx("queue.lead")}</p>}
        meta={rows ? <Chip tone="muted">{tx("queue.count", { n: rows.length, w: waiting })}</Chip> : null}
      />
      <div className="b-filterbar">
        <div className="b-filterbar-chips">
          <button type="button" className={`b-chip${why ? " is-on" : ""}`} aria-pressed={why} onClick={() => setWhy((v) => !v)}>
            {tx("queue.why.toggle")}
          </button>
        </div>
        <div className="b-filterbar-end">
          <Button size="md" onClick={() => void load()}>
            {tx("common.action.refresh")}
          </Button>
        </div>
      </div>
      <div className="queue-band">
        <Alert tone="info">{tx("queue.rule")}</Alert>
      </div>
      {moveErr ? <Alert tone="error">{moveErr}</Alert> : null}
      {err ? <Alert tone="error">{tx("queue.loadFailed", { err })}</Alert> : null}
      {!rows && !err ? <Loading /> : null}
      {rows ? <QueueTable rows={rows} projectName={name} why={why} busy={busy} onMove={(f, t) => void move(f, t)} /> : null}
    </AppShell>
  );
}
